import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import util from 'preact-util';

import NsfCompetitionMap from './map';
import { Championship } from './list';
import { RsvpButtons, RsvpPeople } from './rsvp';
import { InvitesReceived, InvitesSent } from './invites';
import { openUserProfileDrawer } from '../../lib/userProfile';
import {
    badgeFor, formatDateNo, hasCoordinates, competitionIcsUrl, sanitizeDescriptionHtml, describeChange, nsfDetailsUrl,
} from '../../lib/nsfCompetitionUi';

// Detail view of one mirrored NSF competition: fields, venue map, change log,
// and the «Varsle meg om lignende» shortcut that opens the watch drawer prefilled.
// Used on /terminliste/:id (pill back button) and inside the detail drawer
// (inDrawer: link to the page + copy link instead).
@observer
class NsfCompetitionDetail extends Component {
    constructor(props) {
        super(props);
        this.state = { linkCopied: false, reminding: false };
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.id !== this.props.id) {
            this.load();
        }
    }

    load = () => {
        const { nsfCompetitionStore, nsfCompetitionRsvpStore, nsfCompetitionInviteStore } = this.props.stores;
        if (this.props.id) {
            nsfCompetitionStore.loadOne(this.props.id);
            if (typeof nsfCompetitionStore.loadConvention === 'function') {
                nsfCompetitionStore.loadConvention(this.props.id);
            }
            if (nsfCompetitionRsvpStore) {
                nsfCompetitionRsvpStore.loadFor([this.props.id]);
            }
            if (nsfCompetitionInviteStore && this.loggedIn()) {
                nsfCompetitionInviteStore.loadFor([this.props.id]);
            }
        }
    }

    loggedIn() {
        const { userStore } = this.props.stores;
        return !!(userStore && userStore.user && userStore.user.id);
    }

    answerInvite = async (invite, status) => {
        const { nsfCompetitionInviteStore, nsfCompetitionRsvpStore } = this.props.stores;
        const id = parseInt(this.props.id, 10);
        const ok = await nsfCompetitionInviteStore.answer(invite.id, status, id);
        if (ok && nsfCompetitionRsvpStore) {
            nsfCompetitionRsvpStore.loadFor([id]);
        }
    }

    remind = async () => {
        const { nsfCompetitionInviteStore } = this.props.stores;
        this.setState({ reminding: true });
        await nsfCompetitionInviteStore.remind(parseInt(this.props.id, 10));
        this.setState({ reminding: false });
    }

    openInvite = () => {
        const { appState, nsfCompetitionStore } = this.props.stores;
        const { inDrawer, drawerLevel = 1 } = this.props;
        const row = nsfCompetitionStore.nsfCompetition || {};
        appState.openDrawer('nsfCompetitionInvite', { id: row.id, title: row.title }, inDrawer ? drawerLevel + 1 : 1);
    }

    setRsvp = (status) => {
        const { nsfCompetitionRsvpStore } = this.props.stores;
        if (nsfCompetitionRsvpStore && this.props.id) {
            nsfCompetitionRsvpStore.setStatus(parseInt(this.props.id, 10), status);
        }
    }

    openProfile = (person) => {
        const { appState } = this.props.stores;
        openUserProfileDrawer(appState, {
            userId: person.id, name: person.name, image: person.image, faceBox: person.faceBox,
        });
    }

    openWatch = () => {
        const { appState, nsfCompetitionStore } = this.props.stores;
        const { inDrawer, drawerLevel = 1 } = this.props;
        const row = nsfCompetitionStore.nsfCompetition || {};
        appState.openDrawer('nsfCompetitionWatch', {
            prefill: {
                name: row.title ? `Lignende: ${row.title}`.slice(0, 120) : '',
                branchNames: row.branchNames || [],
                disciplineGroups: row.disciplineGroups || [],
                kretsNames: row.kretsName ? [row.kretsName] : [],
            },
        }, inDrawer ? drawerLevel + 1 : 1);
    }

    pageUrl() {
        const { id } = this.props;
        if (typeof window === 'undefined') {
            return `/terminliste/${id}`;
        }
        return `${window.location.origin}${window.location.pathname}#/terminliste/${id}`;
    }

    shareText() {
        const { nsfCompetitionStore } = this.props.stores;
        const row = nsfCompetitionStore.nsfCompetition || {};
        return `Blir du med på ${row.title || 'stevnet'} ${formatDateNo(row.startDate)}?`;
    }

    /** Web Share API where it exists (phones); otherwise the link goes to the clipboard. */
    share = async () => {
        const { nsfCompetitionStore } = this.props.stores;
        const row = nsfCompetitionStore.nsfCompetition || {};
        if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
            try {
                await navigator.share({ title: row.title || '', text: this.shareText(), url: this.pageUrl() });
                return;
            } catch (e) {
                // Cancelled or not allowed: fall back to copying the link.
            }
        }
        await this.copyLink();
    }

    copyLink = async () => {
        if (typeof navigator === 'undefined' || !navigator.clipboard) {
            return;
        }
        try {
            await navigator.clipboard.writeText(this.pageUrl());
            this.setState({ linkCopied: true });
            clearTimeout(this.copiedTimer);
            this.copiedTimer = setTimeout(() => this.setState({ linkCopied: false }), 2000);
        } catch (e) {
            // Clipboard denied: the page link next to the button still works.
        }
    }

    componentWillUnmount() {
        clearTimeout(this.copiedTimer);
    }

    render() {
        const {
            nsfCompetitionStore, nsfCompetitionRsvpStore, nsfCompetitionInviteStore,
        } = this.props.stores;
        const { inDrawer, apiServer = util.getApiServer() || '' } = this.props;
        const { linkCopied, reminding } = this.state;
        const row = nsfCompetitionStore.nsfCompetition || {};
        // The store still holds the previous stevne until loadOne returns: show a spinner, not stale data.
        if (row.id && this.props.id && String(row.id) !== String(this.props.id)) {
            return (
                <div class='text-body-secondary py-3'>
                    <i class='fa-solid fa-spinner fa-spin me-1' /> …
                </div>
            );
        }
        if (!row.id) {
            return (
                <div class='alert alert-light border text-body-secondary'>
                    <Text id='terminliste.notFound'>Competition not found.</Text>
                </div>
            );
        }
        const badge = badgeFor(row);
        const loggedIn = this.loggedIn();
        const invites = loggedIn && nsfCompetitionInviteStore ? nsfCompetitionInviteStore.summaryFor(row.id) : { received: [], sent: [] };
        const nsfUrl = nsfDetailsUrl(row);
        const convention = nsfCompetitionStore.convention && nsfCompetitionStore.convention.id ? nsfCompetitionStore.convention : null;
        const events = Array.isArray(row.events) ? row.events : [];
        const heats = Array.isArray(row.heats) ? row.heats : [];
        const log = Array.isArray(row.changeLog) ? [...row.changeLog].reverse() : [];
        const rsvp = nsfCompetitionRsvpStore ? nsfCompetitionRsvpStore.summaryFor(row.id) : undefined;
        return (
            <div class='container-fluid px-0'>
                {!inDrawer && (
                    <a class='btn btn-sm btn-outline-secondary rounded-pill mb-2' href='/terminliste'>
                        <i class='fa-solid fa-arrow-left' /> <Text id='terminliste.backToList'>Back to the list</Text>
                    </a>
                )}
                <div class='d-flex flex-wrap gap-2 mb-2'>
                    {inDrawer && (
                        <a class='btn btn-sm btn-outline-secondary rounded-pill' href={`/terminliste/${row.id}`}>
                            <i class='fa-solid fa-up-right-from-square me-1' />
                            <Text id='terminliste.openPage'>Open own page</Text>
                        </a>
                    )}
                    <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill nsf-share' onClick={this.share}>
                        <i class={`fa-solid ${linkCopied ? 'fa-check' : 'fa-share-nodes'} me-1`} />
                        {linkCopied
                            ? <Text id='terminliste.linkCopied'>Link copied</Text>
                            : <Text id='terminliste.share'>Share</Text>}
                    </button>
                    {loggedIn && (
                        <button type='button' class='btn btn-sm btn-outline-primary rounded-pill nsf-invite' onClick={this.openInvite}>
                            <i class='fa-solid fa-user-plus me-1' />
                            <Text id='terminliste.invite.button'>Invite club mates</Text>
                            {invites.sent.length > 0 && <span class='badge text-bg-light ms-1'>{invites.sent.length}</span>}
                        </button>
                    )}
                </div>
                <h3 class={badge === 'cancelled' ? 'text-decoration-line-through text-body-secondary' : ''}>
                    {row.title}
                    <Championship typeName={row.competitionTypeName} />
                    {badge === 'cancelled' && <span class='badge text-bg-danger ms-2 align-middle'><Text id='terminliste.cancelled'>Cancelled</Text></span>}
                </h3>
                <div class='row g-3'>
                    <div class='col-12 col-lg-6'>
                        <dl class='row mb-0 small'>
                            <dt class='col-4'><Text id='terminliste.date'>Date</Text></dt>
                            <dd class='col-8'>{formatDateNo(row.startDate)}{row.endDate ? ` – ${formatDateNo(row.endDate)}` : ''}</dd>
                            <dt class='col-4'><Text id='terminliste.organizer'>Organiser</Text></dt>
                            <dd class='col-8'>{row.organizationName}</dd>
                            <dt class='col-4'><Text id='terminliste.krets'>Krets</Text></dt>
                            <dd class='col-8'>{row.kretsName || '–'}</dd>
                            <dt class='col-4'><Text id='terminliste.type'>Type</Text></dt>
                            <dd class='col-8'>{row.competitionTypeName || '–'}</dd>
                            <dt class='col-4'><Text id='terminliste.branch'>Gren</Text></dt>
                            <dd class='col-8'>{(row.branchNames || []).join(', ') || '–'}</dd>
                            <dt class='col-4'><Text id='terminliste.disciplines'>Disciplines</Text></dt>
                            <dd class='col-8'>{(row.disciplineNames || []).join(', ') || '–'}</dd>
                            <dt class='col-4'><Text id='terminliste.place'>Place</Text></dt>
                            <dd class='col-8'>
                                {[row.facilityName, row.facilityAddress, row.facilityZipCode, row.facilityCity].filter(Boolean).join(', ') || '–'}
                                {!hasCoordinates(row) && <div class='text-body-secondary'><Text id='terminliste.noCoordinates'>No map position</Text></div>}
                                {hasCoordinates(row) && row.coordsSource === 'geocoded' && (
                                    <div class='text-body-secondary'><Text id='terminliste.geocodedPosition'>Position from address (approximate)</Text></div>
                                )}
                            </dd>
                            {row.competitionNumber > 0 && <>
                                <dt class='col-4'>Stevnenr</dt>
                                <dd class='col-8'>{row.competitionNumber}</dd>
                            </>}
                            {loggedIn && (row.contactEmail || row.contactPhoneNumber || row.contactWebsite) && <>
                                <dt class='col-4'><Text id='terminliste.contact'>Contact</Text></dt>
                                <dd class='col-8'>
                                    {row.contactEmail && <div><a href={`mailto:${row.contactEmail}`}>{row.contactEmail}</a></div>}
                                    {row.contactPhoneNumber && <div>{row.contactPhoneNumber}</div>}
                                    {row.contactWebsite && <div><a href={row.contactWebsite} target='_blank' rel='noopener noreferrer'>{row.contactWebsite}</a></div>}
                                </dd>
                            </>}
                        </dl>
                        {row.description && (
                            <div class='small mt-2' dangerouslySetInnerHTML={{ __html: sanitizeDescriptionHtml(row.description) }} />
                        )}
                        {events.length > 0 && (
                            <div class='mt-3 nsf-events'>
                                <h6 class='mb-1'><Text id='terminliste.events'>Events and entry fees</Text></h6>
                                <table class='table table-sm small mb-0'>
                                    <thead>
                                        <tr>
                                            <th><Text id='terminliste.event'>Event</Text></th>
                                            <th><Text id='terminliste.class'>Class</Text></th>
                                            <th class='text-end'><Text id='terminliste.entryFee'>Entry fee</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {events.map((event, idx) => (
                                            <tr key={`${event.disciplineName}-${event.className}-${idx}`}>
                                                <td>{event.disciplineName || '–'}</td>
                                                <td>{event.className || '–'}</td>
                                                <td class='text-end text-nowrap'>{Number.isFinite(event.entryFee) ? `${event.entryFee} kr` : '–'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {heats.length > 0 && (
                            <div class='mt-3 nsf-heats'>
                                <h6 class='mb-1'><Text id='terminliste.heats'>Heats and shooting times</Text></h6>
                                <table class='table table-sm small mb-0'>
                                    <thead>
                                        <tr>
                                            <th><Text id='terminliste.heat'>Heat</Text></th>
                                            <th><Text id='terminliste.heatTime'>Time</Text></th>
                                            <th><Text id='terminliste.disciplines'>Disciplines</Text></th>
                                            <th class='text-end'><Text id='terminliste.heatCapacity'>Places</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {heats.map((heat, idx) => (
                                            <tr key={`${heat.name}-${idx}`}>
                                                <td>{heat.name || `${idx + 1}`}</td>
                                                <td class='text-nowrap'>{heat.startTime ? formatDateNo(heat.startTime) : '–'}</td>
                                                <td>{(heat.disciplineNames || []).join(', ') || '–'}</td>
                                                <td class='text-end text-nowrap'>
                                                    {Number.isFinite(heat.capacity) ? heat.capacity : '–'}
                                                    {heat.targets > 0 && <span class='text-body-tertiary'> · {heat.targets} <Text id='terminliste.targets'>targets</Text></span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div class='mt-3'>
                            <InvitesReceived received={invites.received} onAnswer={this.answerInvite} />
                            <RsvpButtons summary={rsvp} loggedIn={loggedIn} onSet={this.setRsvp} />
                            {!loggedIn && rsvp && (rsvp.counts.going + rsvp.counts.interested + rsvp.counts.maybe) > 0 && (
                                <div class='small text-body-secondary'><Text id='terminliste.rsvp.loginToRespond'>Log in to say if you are going.</Text></div>
                            )}
                            <div class='mt-2'><RsvpPeople summary={rsvp} onOpenProfile={this.openProfile} /></div>
                            <InvitesSent sent={invites.sent} onRemind={this.remind} reminding={reminding} />
                        </div>
                        <div class='d-flex flex-wrap gap-2 mt-2'>
                            <button type='button' class='btn btn-sm btn-success' onClick={this.openWatch}>
                                <i class='fa-duotone fa-bell me-1' />
                                <Text id='terminliste.watchSimilar'>Alert me about similar competitions</Text>
                            </button>
                            <a class='btn btn-sm btn-outline-secondary' href={competitionIcsUrl({ apiServer, id: row.id })} download>
                                <i class='fa-duotone fa-calendar-plus me-1' />
                                <Text id='terminliste.addToCalendar'>Add to calendar</Text>
                            </a>
                            {convention && (
                                <a class='btn btn-sm btn-warning nsf-results-link' href={`/convention/${convention.id}/results`}>
                                    <i class='fa-duotone fa-trophy me-1' />
                                    <Text id='terminliste.results'>Results</Text>
                                </a>
                            )}
                            {nsfUrl && (
                                <a class='btn btn-sm btn-outline-primary nsf-details-link' href={nsfUrl} target='_blank' rel='noopener noreferrer'>
                                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                                    <Text id='terminliste.nsfDetails'>Details and registration at NSF</Text>
                                </a>
                            )}
                        </div>
                    </div>
                    <div class='col-12 col-lg-6'>
                        {hasCoordinates(row) && <NsfCompetitionMap rows={[row]} height='320px' />}
                        {log.length > 0 && (
                            <div class='mt-3'>
                                <h6><Text id='terminliste.changeLog'>Changes</Text></h6>
                                <ul class='list-group list-group-flush small'>
                                    {log.map((entry, idx) => {
                                        const detail = describeChange(entry);
                                        return (
                                            <li key={`${entry.date}-${idx}`} class='list-group-item px-0 py-1'>
                                                <div class='d-flex justify-content-between'>
                                                    <span class='fw-semibold'><Text id={`terminliste.change-${entry.type}`}>{entry.type}</Text></span>
                                                    <span class='text-body-secondary'>{formatDateNo(entry.date)}</span>
                                                </div>
                                                {detail && <div class='text-body-secondary'>{detail}</div>}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default NsfCompetitionDetail;
